import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import FieldEditor from "../components/FieldEditor";
import FormPreview from "../components/FormPreview";
import ExcelReuploadModal from "../components/ExcelReuploadModal";
import MandatoryNotesCard from "../components/MandatoryNotesCard";
import {
  PALETTE,
  MAX_FIELDS,
  normalizeFieldsForApi,
  generateFieldId,
  generateFieldLabel,
} from "../utils/formBuilderUtils";
import { useFieldValidation } from "../utils/useFieldValidation";
import api from "../api";
import { useAuth } from "../auth/AuthContext";
import { showError, showSuccess } from "../components/AppToast";
import { useFormikContext } from "formik";
import {
  CheckCircle,
  Circle,
  AlertTriangle,
  FileSpreadsheet,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Check,
  Eye,
  EyeOff,
  Settings,
  GripVertical,
  X,
  ListChecks,
  Receipt,
  Mail,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

// Maximum number of fields that can have "receipt" enabled at once, whether
// toggled individually in FieldEditor or via the "Enable receipt for all fields" global
// toggle below.
const RECEIPT_FIELD_LIMIT = 30;

// Reserved id — must match the mandatory-amount id in formBuilderUtils.js.
// Amount is the ONLY true mandatory/locked field now: always present,
// always required, never removable. It's freshly created for every form
// (never sourced from or overwritten by Excel columns), because its value
// is always computed (split/primary amount logic). Its receiptEnabled is
// always true and can't be turned off — it's always included in the receipt.
//
// Email and Phone are NOT mandatory anymore. They behave like any other
// optional field: available in the palette, not auto-added to a new form,
// freely required/optional, and removable like any other field.
const MANDATORY_AMOUNT_ID = "mandatory-amount";

const MANDATORY_FIELDS = [
  {
    id: MANDATORY_AMOUNT_ID,
    type: "amount",
    label: "Amount",
    required: true,
    mandatory: true,
    readOnly: false,
    fixedValueEnabled: false,
    fixedValue: "",
    receiptEnabled: true,
    receiptOrder: null,
  },
];

const STEP_STATE = { IDLE: "idle", ACTIVE: "active", DONE: "done", ERROR: "error" };

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function base64ToFile(base64String, filename = "logo.png") {
  try {
    const parts = base64String.split(",");
    const mimeMatch = parts[0].match(/:(.*?);/);
    if (!mimeMatch) throw new Error("Invalid base64");
    const mime = mimeMatch[1];
    const binaryString = atob(parts[1]);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  } catch (e) {
    console.error(e);
    return null;
  }
}

function pollJobStatus({ formId, onProgress, signal }) {
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = async () => {
      if (signal?.aborted) return reject(new Error("cancelled"));
      if (Date.now() - started > POLL_TIMEOUT_MS)
        return reject(new Error("Job status check timed out after 5 minutes."));
      try {
        const token = localStorage.getItem("jwt");
        const res = await api.post(`/bulk/job-status/${formId}`, {}, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const data = res?.data ?? res;
        const status = String(data?.status ?? "").toUpperCase();
        onProgress({
          processed: data?.processed ?? 0,
          success: data?.success ?? 0,
          failed: data?.failed ?? 0,
          percentage: data?.percentage ?? 0,
          reason: data?.reason ?? "",
          status,
        });
        if (status === "COMPLETED") return resolve(data);
        if (status === "FAILED")
          return reject(new Error(data?.reason && data.reason !== "NA" ? data.reason : "Excel processing failed on the server."));
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (signal?.aborted) return reject(new Error("cancelled"));
        reject(err);
      }
    };
    setTimeout(tick, 1500);
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// ModalProgressStepper (unchanged)
// ─────────────────────────────────────────────────────────────────────────────
function ModalProgressStepper({ steps }) {
  return (
    <div className="fs-stepper">
      {steps.map((step, i) => (
        <React.Fragment key={step.id}>
          <ModalStepItem step={step} />
          {i < steps.length - 1 && (
            <div className={`fs-step-connector ${step.state === STEP_STATE.DONE ? "fs-step-connector-filled" : ""}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

function ModalStepItem({ step }) {
  const isActive = step.state === STEP_STATE.ACTIVE;
  const isDone = step.state === STEP_STATE.DONE;
  const isError = step.state === STEP_STATE.ERROR;
  const labelClass = ["fs-step-label", isActive ? "fs-step-label-active" : "", isDone ? "fs-step-label-done" : "", isError ? "fs-step-label-error" : ""].filter(Boolean).join(" ");
  return (
    <div className="fs-step-item">
      <div className="fs-step-icon-wrap">
        {isActive && <span className="fs-step-pulse" />}
        {isDone && <div className="fs-step-circle-done"><Check size={16} color="#16a34a" strokeWidth={2.5} /></div>}
        {isError && <div className="fs-step-circle-error"><XCircle size={18} color="#dc2626" /></div>}
        {isActive && <div className="fs-step-circle-active" />}
        {!isDone && !isError && !isActive && <div className="fs-step-circle-idle"><div className="fs-step-circle-idle-dot" /></div>}
      </div>
      <span className={labelClass}>{step.label}</span>
      {step.id === "process" && step.state === STEP_STATE.ACTIVE && step.percentage != null && (
        <span className="fs-step-pct">{step.percentage}%</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SaveProgressModal (unchanged logic)
// ─────────────────────────────────────────────────────────────────────────────
function SaveProgressModal({ isOpen, runKey, slug, doCreateForm, hasExcel, onClose, onFullSuccess }) {
  const makeInitialSteps = useCallback(() => {
    if (!hasExcel) return [
      { id: "form", label: "Creating Form", state: STEP_STATE.IDLE },
      { id: "done", label: "Complete", state: STEP_STATE.IDLE },
    ];
    return [
      { id: "form", label: "Creating Form", state: STEP_STATE.IDLE },
      { id: "process", label: "Processing Excel Data", state: STEP_STATE.IDLE, percentage: null },
    ];
  }, [hasExcel]);

  const [steps, setSteps] = useState(makeInitialSteps);
  const [phase, setPhase] = useState("running");
  const [statusMsg, setStatusMsg] = useState("Saving your form configuration…");
  const [createdFormId, setCreatedFormId] = useState(null);
  const [reuploadOpen, setReuploadOpen] = useState(false);
  const [jobStats, setJobStats] = useState(null);
  const abortRef = useRef(null);
  const hasRunRef = useRef(false);
  // const {token,role} = useAuth();

  useEffect(() => { hasRunRef.current = false; }, [runKey]);

  const setStepState = useCallback(
    (stepId, newState, extra = {}) =>
      setSteps((prev) => prev.map((s) => (s.id === stepId ? { ...s, state: newState, ...extra } : s))),
    [],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (hasRunRef.current) return;
    hasRunRef.current = true;
    setSteps(makeInitialSteps());
    setPhase("running");
    setStatusMsg("Saving your form configuration…");
    setCreatedFormId(null);
    setReuploadOpen(false);
    setJobStats(null);
    const abortController = new AbortController();
    abortRef.current = abortController;
    const run = async () => {
      setStepState("form", STEP_STATE.ACTIVE);
      let formId;
      try {
        const result = await doCreateForm();
        if (abortController.signal.aborted) return;
        formId = result?.createdId;
        setCreatedFormId(formId);
        setStepState("form", STEP_STATE.DONE);
      } catch (err) {
        if (abortController.signal.aborted) return;
        setStepState("form", STEP_STATE.ERROR);
        setStatusMsg(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Failed to save form.");
        setPhase("form_failed");
        return;
      }
      if (!hasExcel) {
        setStepState("done", STEP_STATE.ACTIVE);
        setStatusMsg("Wrapping up…");
        await new Promise((r) => setTimeout(r, 400));
        if (abortController.signal.aborted) return;
        setStepState("done", STEP_STATE.DONE);
        setStatusMsg("Form saved successfully!");
        setPhase("success");
        onFullSuccess?.();
        return;
      }
      setStepState("process", STEP_STATE.ACTIVE);
      setStatusMsg("Processing records…");
      try {
        await pollJobStatus({
          formId,
          signal: abortController.signal,
          onProgress: (stats) => {
            setJobStats(stats);
            setStepState("process", STEP_STATE.ACTIVE, { percentage: stats.percentage });
            setStatusMsg(`Processing… ${stats.processed} records (${stats.percentage}%)`);
          },
        });
        if (abortController.signal.aborted) return;
        setStepState("process", STEP_STATE.DONE, { percentage: 100 });
        setJobStats((prev) => (prev ? { ...prev, percentage: 100 } : null));
        setStatusMsg("Form published and data processed successfully!");
        localStorage.removeItem("excel_rows");
        localStorage.removeItem("excel_file_meta");
        localStorage.removeItem("excel_fields");
        setPhase("success");
        onFullSuccess?.();
      } catch (err) {
        if (abortController.signal.aborted) return;
        setStepState("process", STEP_STATE.ERROR);
        setStatusMsg(err?.response?.data?.error || err?.response?.data?.message || err?.message || "Data processing failed.");
        setPhase("excel_failed");
      }
    };
    run();
    return () => { abortController.abort(); };
  }, [isOpen]);

  const isRunning = phase === "running";
  const isSuccess = phase === "success";
  const isExcelFailed = phase === "excel_failed";
  const isFormFailed = phase === "form_failed";
  const isDone = !isRunning;

  const bandClass = isSuccess ? "fs-spm-band fs-spm-band-success" : isFormFailed || isExcelFailed ? "fs-spm-band fs-spm-band-error" : "fs-spm-band";
  const heroIconWrapClass = isSuccess ? "fs-spm-icon-wrap fs-spm-icon-wrap-success" : isFormFailed ? "fs-spm-icon-wrap fs-spm-icon-wrap-failed" : isExcelFailed ? "fs-spm-icon-wrap fs-spm-icon-wrap-excel-failed" : "fs-spm-icon-wrap fs-spm-icon-wrap-running";
  const titleText = isSuccess ? "All done!" : isFormFailed ? "Something went wrong" : isExcelFailed ? "Processing failed" : "Publishing…";

  return (
    <>
      <Modal isOpen={isOpen && !reuploadOpen} centered backdrop={isRunning ? "static" : true} keyboard={!isRunning} className="fs-save-progress-dialog">
        <ModalBody className="fs-save-progress-body">
          <div className={bandClass} />
          <div className="fs-spm-content">
            <div className="fs-spm-hero">
              <div className={heroIconWrapClass}>
                {isSuccess && <CheckCircle2 size={36} color="#16a34a" strokeWidth={2} />}
                {isFormFailed && <XCircle size={36} color="#dc2626" strokeWidth={2} />}
                {isExcelFailed && <AlertTriangle size={34} color="#d97706" strokeWidth={2} />}
                {isRunning && <FileSpreadsheet size={34} color="#6366f1" strokeWidth={1.8} />}
              </div>
              <h5 className="fs-spm-title">{titleText}</h5>
              <p className="fs-spm-subtitle">{statusMsg}</p>
            </div>
            <ModalProgressStepper steps={steps} />
            {hasExcel && jobStats && jobStats.success >= 50 && (
              <div className="fs-job-stats">
                <div className="fs-job-stat">
                  <span className="fs-job-stat-num fs-job-stat-num-blue">{jobStats.processed}</span>
                  <span className="fs-job-stat-label">Processed</span>
                </div>
                <div className="fs-job-stat-divider" />
                <div className="fs-job-stat">
                  <span className="fs-job-stat-num fs-job-stat-num-green">{jobStats.success}</span>
                  <span className="fs-job-stat-label">Success</span>
                </div>
                <div className="fs-job-stat-divider" />
                <div className="fs-job-stat">
                  <span className={`fs-job-stat-num ${jobStats.failed > 0 ? "fs-job-stat-num-red" : "fs-job-stat-num-grey"}`}>{jobStats.failed}</span>
                  <span className="fs-job-stat-label">Failed</span>
                </div>
                {jobStats.percentage < 100 && (
                  <div className="fs-job-progress-wrap">
                    <div className="fs-job-progress-track">
                      <div className="fs-job-progress-fill" style={{ width: `${jobStats.percentage}%` }} />
                    </div>
                    <span className="fs-job-progress-pct">{jobStats.percentage}%</span>
                  </div>
                )}
              </div>
            )}
            {isExcelFailed && (
              <div className="fs-excel-fail-card">
                <div className="fs-excel-fail-icon-wrap"><AlertTriangle size={18} color="#d97706" /></div>
                <div className="fs-excel-fail-content">
                  <div className="fs-excel-fail-title">Excel processing failed</div>
                  <div className="fs-excel-fail-desc">{statusMsg || "Your form was saved. The data processing failed — retry below."}</div>
                </div>
                <button type="button" className="fs-excel-fail-retry-btn" onClick={() => setReuploadOpen(true)}>
                  <RefreshCw size={13} /> Retry
                </button>
              </div>
            )}
            {isFormFailed && (
              <div className="fs-form-failed-note">
                <XCircle size={18} color="#dc2626" style={{ flexShrink: 0 }} />
                <span className="fs-form-failed-text">{statusMsg || "Please try again. If the problem persists, contact support."}</span>
              </div>
            )}
          </div>
          {isDone && (
            <div className={`fs-spm-footer ${isExcelFailed ? "fs-spm-footer-spread" : ""}`}>
              <button type="button" className={`fs-spm-btn-primary ${isSuccess ? "fs-spm-btn-success" : ""}`} onClick={onClose}>
                {isSuccess ? <><Check size={15} /> Done</> : "Close"}
              </button>
            </div>
          )}
        </ModalBody>
      </Modal>
      <ExcelReuploadModal
        isOpen={reuploadOpen}
        toggle={() => setReuploadOpen(false)}
        formId={createdFormId}
        slug={slug}
        onSuccess={() => {
          setReuploadOpen(false);
          setPhase("success");
          setStatusMsg("Data re-uploaded and processed successfully!");
          setStepState("process", STEP_STATE.DONE, { percentage: 100 });
          onFullSuccess?.();
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main FormSetup
// ─────────────────────────────────────────────────────────────────────────────
export default function FormSetup({ onFormSaved, onFieldsSelected, formik }) {
  const { id } = useParams();
  const { token } = useAuth();

  const [fields, setFields] = useState([...MANDATORY_FIELDS]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [previewStatus, setPreviewStatus] = useState(false);
  const [environment, setEnvironment] = useState("U");
  const [savedFormId, setSavedFormId] = useState(null);
  const [formSaved, setFormSaved] = useState(false);
  const [excelDone, setExcelDone] = useState(false);
  const [showSaveProgressModal, setShowSaveProgressModal] = useState(false);
  const [saveRunKey, setSaveRunKey] = useState(0);

  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [showLivePreview, setShowLivePreview] = useState(false);

  const {
    hasFieldValidationErrors,
    handleFieldValidationChange,
    clearFieldError,
  } = useFieldValidation();

  const role = atob(localStorage.getItem("role"));

  useEffect(() => {
    if (onFieldsSelected) onFieldsSelected(fields.map((f) => f.label));
  }, [fields, onFieldsSelected]);

  const { values } = useFormikContext();
  // const formik = useFormikContext();
  // const { values } = formik;
  const {
    title = "", slug = "", description = "", merchantId = "",
    username = "", password = "", customReceipt = false,
    merchPhone = "", merchEmail = "", stakeholderNotification = false,
    stakeholderEmails = [], merchantNotification = false, mode = "",
    excelSearchKey = "", excelColumns = [], excelRows = [],
  } = values;

  useEffect(() => { if (token) api.setToken(token); }, [token]);


  useEffect(() => {
    if (!excelColumns?.length) return;

    // Amount is the only reserved/locked id — it's always freshly created
    // and never sourced from Excel. Email/Phone columns from Excel (if any)
    // now flow through as ordinary optional "extras", same as any other
    // Excel column.
    const RESERVED_IDS = new Set([MANDATORY_AMOUNT_ID]);
    const amountDefault = MANDATORY_FIELDS.find((m) => m.id === MANDATORY_AMOUNT_ID);
    const resolvedAmount = { ...amountDefault };

    const consumedIds = new Set(RESERVED_IDS);

    const extras = excelColumns
      .filter((f) => !consumedIds.has(f.id))
      .map((f) => ({
        ...f,
        fromExcel: true,
        mandatory: false,
        readOnly: f.readOnly ?? false,
        receiptEnabled: f.receiptEnabled ?? false,
        receiptOrder: f.receiptOrder ?? null,
      }));

    const all = [resolvedAmount, ...extras];
    const skIdx = excelSearchKey ? all.findIndex((f) => f.id === excelSearchKey || f.label?.toLowerCase() === excelSearchKey.toLowerCase()) : -1;
    let final;
    if (skIdx > 0) { const [sf] = all.splice(skIdx, 1); final = [{ ...sf, isSearchKey: true, mandatory: true }, ...all]; }
    else if (skIdx === 0) { final = [{ ...all[0], isSearchKey: true, mandatory: true }, ...all.slice(1)]; }
    else { final = all; }
    setFields(final);
  }, []);

  // ── Drag & drop (custom — gives us full control over id/label generation) ──
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;

    if (source.droppableId === "palette" && destination.droppableId === "canvas") {
      if (fields.length >= MAX_FIELDS) { showError(`Maximum ${MAX_FIELDS} fields allowed`); return; }
      const src = PALETTE[source.index];

      const newId = generateFieldId(src.type, fields);
      const newLabel = generateFieldLabel(src.label, fields);
      const instance = {
        id: newId, type: src.type, label: newLabel,receiptEmailEnabled: false,
        options: src.options || [], required: false, mandatory: false,
        readOnly: false, splitRequired: false, fixedValueEnabled: false, fixedValue: "",
        receiptEnabled: false, receiptOrder: null,
        cascadeEnabled: false,parentFieldId: null,cascadeLevel: 1,cascadeMappings: {},

      };
      const arr = Array.from(fields);
      // Keep the locked search-key field (if any) pinned at index 0
      const lockedAtFront = fields[0]?.isSearchKey;
      const insertIndex = lockedAtFront ? Math.max(destination.index, 1) : destination.index;
      arr.splice(insertIndex, 0, instance);
      setFields(arr);
      setSelectedFieldId(newId);
      return;
    }

    if (source.droppableId === "canvas" && destination.droppableId === "canvas") {
      const lockedAtFront = fields[0]?.isSearchKey;
      if (lockedAtFront && (source.index === 0 || destination.index === 0)) return;
      const arr = Array.from(fields);
      const [moved] = arr.splice(source.index, 1);
      arr.splice(destination.index, 0, moved);
      setFields(arr);
    }
  };

  // ── Duplicate label check ─────────────────────────────────────────────────
  const hasDuplicateLabel = useCallback(
    (label, excludeId) => {
      const normalized = label.trim().toLowerCase();
      return fields.some(
        (f) => f.id !== excludeId && f.label.trim().toLowerCase() === normalized,
      );
    },
    [fields],
  );

  const updateField = (fieldId, newField) => {
  if (hasDuplicateLabel(newField.label, fieldId)) {
    showError(
      `Duplicate label "${newField.label}" — each field must have a unique label.`
    );
    return;
  }

  setErr("");

  setFields((prev) => {
    const updatedFields = prev.map((f) => {
      if (f.id !== fieldId) {
        return f;
      }

      const merged = {
        ...f,
        ...newField,
      };

      if (f.mandatory) {
        merged.required = true;
        merged.mandatory = true;
      }

      if (
        f.type === "amount" &&
        f.mandatory
      ) {
        merged.receiptEnabled = true;
      }

      return merged;
    });

    const updatedField =
      updatedFields.find(
        (f) => f.id === fieldId
      );

    if (
      !updatedField ||
      !Array.isArray(updatedField.options)
    ) {
      return updatedFields;
    }

    return updatedFields.map((f) => {
      let updatedMappings =
        f.cascadeMappings;

      //
      // Parent option removed cleanup
      //
      if (
        f.parentFieldId === fieldId &&
        f.cascadeMappings
      ) {
        updatedMappings = Object.fromEntries(
          Object.entries(
            f.cascadeMappings
          ).filter(([parentOption]) =>
            updatedField.options.includes(
              parentOption
            )
          )
        );
      }

      //
      // Child option removed cleanup
      //
    //
// Child option removed cleanup
//
if (
  f.id === fieldId &&
  f.cascadeMappings
) {
  updatedMappings =
    Object.fromEntries(
      Object.entries(
        updatedMappings || {}
      ).map(
        ([parentOption, values]) => [
          parentOption,
          (values || []).filter(
            (childValue) =>
              updatedField.options?.includes(
                childValue
              )
          ),
        ]
      )
    );
}

      return {
        ...f,
        cascadeMappings:
          updatedMappings,
      };
    });
  });
};

  const removeField = (fieldId) => {
  setFields((prev) =>
    prev
      .filter(
        (f) =>
          f.mandatory ||
          f.id !== fieldId
      )
      .map((f) => {
        if (
          f.parentFieldId === fieldId
        ) {
          return {
            ...f,
            cascadeEnabled: false,
            parentFieldId: null,
            cascadeLevel: 1,
            cascadeMappings: {},
          };
        }

        return f;
      })
  );

  clearFieldError(fieldId);

  if (selectedFieldId === fieldId) {
    setSelectedFieldId(null);
  }
};

  // ── Global bulk toggles ─────────────────────────────────────────────────
  // Both are real ON/OFF switches now: their current state reflects whether
  // every field already has that property set, and clicking flips it for
  // every field (rather than only ever turning things on).
  const allRequired = fields.length > 0 && fields.every((f) => !!f.required);
  const allReceiptEnabled = fields.length > 0 && fields.every((f) => !!f.receiptEnabled);
  // const hasEmailField = fields.some(
  //   (f) => f.type?.toLowerCase() === "email"
  // );

  // "Required for All" toggle. Mandatory fields (Amount) are always
  // required regardless of this toggle's state.
  const toggleRequiredForAll = () => {
    if (!fields.length) return;
    if (allRequired) {
      setFields((prev) => prev.map((f) => ({ ...f, required: f.mandatory ? true : false })));
    } else {
      setFields((prev) => prev.map((f) => ({ ...f, required: true })));
    }
  };

  // "Enable receipt for all fields" toggle. Blocked with an error toast when switching ON
  // if the form has more fields than RECEIPT_FIELD_LIMIT, since receipt can
  // only ever be enabled for up to RECEIPT_FIELD_LIMIT fields at a time.
  // Switching OFF always works — except Amount, which always stays in the
  // receipt no matter what.
  const toggleReceiptForAll = () => {
    if (!fields.length) return;
    if (allReceiptEnabled) {
      setFields((prev) => prev.map((f) => ({
        ...f,
        receiptEnabled: (f.type === "amount" && f.mandatory) ? true : false,
      })));
      return;
    }
    if (fields.length > RECEIPT_FIELD_LIMIT) {
      showError(`Receipt can be enabled for a maximum of ${RECEIPT_FIELD_LIMIT} fields. This form currently has ${fields.length} fields.`);
      return;
    }
    setFields((prev) => prev.map((f) => ({ ...f, receiptEnabled: true })));
  };

  // const toggleEmailReceipt = () => {
  //   if (!hasEmailField) {
  //     showError(
  //       "Add an Email field to enable email receipts."
  //     );
  //     return;
  //   }

  //   formik.setFieldValue(
  //     "customReceipt",
  //     !formik.values.customReceipt
  //   );
  // };
  const selectedField = fields.find((f) => f.id === selectedFieldId) || null;

  const canSave = useMemo(() => {
    if (hasFieldValidationErrors) return false;
    const hasMandatory = MANDATORY_FIELDS.every((m) =>
      fields.some((f) => f.id === m.id || (f.mandatory && f.type === m.type)),
    );
    return (
      title?.trim() && slug?.trim() && fields.length > 0 &&
      (role === "MERCHANT" ? true : merchantId?.trim()) &&
      hasMandatory && username?.trim() && password?.trim() &&

      merchPhone?.trim() && merchEmail?.trim()
    );
  }, [title, slug, fields, merchantId, username, password, merchEmail, merchPhone, role, hasFieldValidationErrors]);
  const receiptFields = fields.filter(
    (f) => f.receiptEnabled
  );

  const fieldsWithOrder = receiptFields.filter(
    (f) =>
      f.receiptOrder !== null &&
      f.receiptOrder !== undefined &&
      f.receiptOrder !== ""
  );

  const fieldsWithoutOrder = receiptFields.filter(
    (f) =>
      f.receiptOrder === null ||
      f.receiptOrder === undefined ||
      f.receiptOrder === ""
  );

  const hasReceiptOrderError =
    fieldsWithOrder.length > 0 &&
    fieldsWithoutOrder.length > 0;
 const validateCascadeMappings = () => {
  for (const field of fields) {
    if (
      field.cascadeEnabled &&
      field.parentFieldId
    ) {
      const mappings =
        field.cascadeMappings || {};

      // At least one mapping must exist
      const hasMapping = Object.values(
        mappings
      ).some(
        (values) =>
          Array.isArray(values) &&
          values.length > 0
      );

      if (!hasMapping) {
        showError(
          `Please configure value mapping for "${field.label}".`
        );
        return false;
      }
      const parentField = fields.find(
  (f) => f.id === field.parentFieldId
);

// if (parentField?.options?.length) {
//   for (const parentOption of parentField.options) {
//     const mappedValues =
//       mappings[parentOption] || [];

//     if (mappedValues.length === 0) {
//       showError(
//         `Please configure mapping for parent option "${parentOption}" in "${field.label}".`
//       );
//       return false;
//     }
//   }
// }

      // No duplicate mapping sets
      // const mappingSets = Object.values(
      //   mappings
      // )
      //   .filter(
      //     (values) =>
      //       Array.isArray(values) &&
      //       values.length > 0
      //   )
      //   .map(
      //     (values) =>
      //       [...values]
      //         .sort()
      //         .join("|")
      //   );

      // if (
      //   new Set(mappingSets).size !==
      //   mappingSets.length
      // ) {
      //   showError(
      //     `Duplicate value mappings found in "${field.label}". Different parent options cannot have the exact same child mappings.`
      //   );
      //   return false;
      // }

      // Required child cannot have any optional ancestor
      if (field.required || field.mandatory) {
        let currentParentId =
          field.parentFieldId;

        while (currentParentId) {
          const parentField =
            fields.find(
              (f) =>
                f.id ===
                currentParentId
            );

          if (!parentField) {
            break;
          }

          if (
            !parentField.required &&
            !parentField.mandatory
          ) {
            showError(
              `"${field.label}" is required, so parent field "${parentField.label}" and all ancestor cascade fields must also be required.`
            );
            return false;
          }

          currentParentId =
            parentField.parentFieldId;
        }
      }
    }
  }

  return true;
};
  const handleSaveClick = () => {
    setErr("");
    if (hasFieldValidationErrors) { setErr("Please fix the validation errors in the form fields before saving."); return; }
    if (!validateCascadeMappings()) {
  return;
}
    if (!title?.trim()) { setErr("Title is required."); return; }
    if (!slug?.trim()) { setErr("Slug is required."); return; }
    if (!(role === "MERCHANT" || role === "VIEWER")) {
      if (!merchantId?.trim()) { setErr("Merchant ID is required."); return; }
    }
    if (!fields.length) { setErr("Add at least one field."); return; }
    if (!username?.trim()) { setErr("Username is required."); return; }
    if (!password?.trim()) { setErr("Password is required."); return; }
    const hasMandatory = MANDATORY_FIELDS.every((m) => fields.some((f) => f.id === m.id || (f.mandatory && f.type === m.type)));
    if (!hasMandatory) { setErr("Amount field is mandatory."); return; }
    const receiptCount = fields.filter((f) => f.receiptEnabled).length;
    //     const receiptFields = fields.filter(
    //   (f) => f.receiptEnabled
    // );

    // const missingReceiptOrder =
    //   receiptFields.some(
    //     (f) => !f.receiptOrder
    //   );

    // if (missingReceiptOrder) {
    //   showError(
    //     "Please select receipt display order for all receipt-enabled fields."
    //   );
    //   return;
    // }


    // User has started configuring order but left some blank
    if (
      fieldsWithOrder.length > 0 &&
      fieldsWithoutOrder.length > 0
    ) {
      showError(
        "Please select receipt display order for all receipt-enabled fields."
      );
      return;
    }
    if (receiptCount > RECEIPT_FIELD_LIMIT) { setErr(`Receipt can be enabled for a maximum of ${RECEIPT_FIELD_LIMIT} fields.`); return; }
    setShowConfirmModal(true);
  };

  const buildFormData = useCallback(() => {
    const fd = new FormData();
    const payload = {
      mode, excelSearchKey, title, slug, description, merchantId,
      form_schema: { fields: normalizeFieldsForApi(fields), customReceipt, stakeholderNotification, stakeholderEmails, merchantNotification, },
      username, password, customReceipt, stakeholderNotification, stakeholderEmails,
      env: environment, merchEmail, merchPhone,
    };

    if (mode !== "manual") payload.excel_values = JSON.stringify(excelRows);
    fd.append("form", new Blob([JSON.stringify(payload)], { type: "application/json" }));
    const storedLogo = sessionStorage.getItem("avatar");
    if (storedLogo) { const lf = base64ToFile(storedLogo, "logo.png"); if (lf) fd.append("logo", lf); }

    const storedSecondaryLogo = sessionStorage.getItem("secondaryLogo");
    if (storedSecondaryLogo) {
      const slf = base64ToFile(
        storedSecondaryLogo,
        "secondary-logo.png"
      );

      if (slf) {
        fd.append(
          "secondaryLogo",
          slf
        );
      }
    }
    return fd;

  }, [mode, excelSearchKey, title, slug, description, merchantId, fields, username, password, customReceipt, stakeholderNotification, stakeholderEmails, merchantNotification, environment, merchEmail, merchPhone, excelRows]);

  const doCreateForm = useCallback(async () => {
    const fd = buildFormData();
    const res = id ? await api.put(`/forms/${id}`, fd) : await api.post("/forms/create-form", fd);
    const createdId = res?.data?.formId || res?.data?.id || res?.id || id;
    setSavedFormId(createdId);
    setFormSaved(true);
    setPreviewStatus(true);
    ["px_multistep_draft", "excel_fields", "excel_file_meta", "excel_rows"].forEach((k) => localStorage.removeItem(k));
    sessionStorage.removeItem("avatar");
    sessionStorage.removeItem("secondaryLogo");
    return { createdId };
  }, [id, buildFormData]);

  const hasExcel = mode === "excel" && !!JSON.stringify(excelRows);

  const onConfirmSave = () => {
    setShowConfirmModal(false);
    setErr("");
    setSaving(true);
    setFormSaved(false);
    setExcelDone(false);
    setSaveRunKey((k) => k + 1);
    setShowSaveProgressModal(true);
  };

  const handlePreview = () => {
    if (!slug?.trim()) { setErr("Slug is required to preview."); return; }
    window.open(`/forms/${slug}`, "_blank", "noopener,noreferrer");
  };

  const mandatoryCount = fields.filter((f) => f.mandatory || f.required).length;
  const optionalCount = fields.filter((f) => !f.mandatory && !f.required).length;
  const receiptCount = fields.filter((f) => f.receiptEnabled).length;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      {mode === "excel" && <MandatoryNotesCard />}
      {err && <div className="alert alert-danger mb-3">{err}</div>}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Row className="g-3">

          {/* ── Palette ──────────────────────────────────────────────────── */}
          <Col md="2">
            <Card className="h-100">
              <CardBody>
                <h6 className="mb-3 text-muted fw-semibold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Field Types
                </h6>
                <Droppable droppableId="palette" isDropDisabled>
                  {(provided) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}>
                      {PALETTE.map((p, idx) => (
                        <Draggable key={`${p.type}-${idx}`} draggableId={`pal-${p.type}-${idx}`} index={idx}>
                          {(prov, snapshot) => (
                            <div
                              ref={prov.innerRef}
                              {...prov.draggableProps}
                              {...prov.dragHandleProps}
                              className={`fb-palette-item ${snapshot.isDragging ? "fb-palette-item-dragging" : ""}`}
                              tabIndex={0}
                            >
                              <GripVertical size={12} className="text-muted me-1" />
                              {p.label}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </CardBody>
            </Card>
          </Col>

          {/* ── Right side: top bar + (Canvas/Properties OR Live Preview) ──── */}
          <Col md="10">
            {/* Top bar: Environment + global toggles + Preview toggle */}
            <Card className="mb-3 fb-toolbar-card">
              <CardBody className="fb-toolbar-body">
                <div className="fb-toolbar-left">
                  <div className="fb-toolbar-env">
                    <label htmlFor="envSelect" className="fb-toolbar-env-label">Environment</label>
                    <select
                      className="fb-env-select"
                      id="envSelect"
                      value={environment}
                      onChange={(e) => setEnvironment(e.target.value)}
                    >
                      <option value="P">Production</option>
                      <option value="U">UAT</option>
                    </select>
                  </div>

                  <div className="fb-toolbar-divider" />

                  <div className="fb-toggle-group">
                    <div
                      className={`fb-toggle-chip ${allRequired ? "fb-toggle-chip-active" : ""} ${!fields.length ? "fb-toggle-chip-disabled" : ""}`}
                      onClick={toggleRequiredForAll}
                      role="switch"
                      aria-checked={allRequired}
                      tabIndex={fields.length ? 0 : -1}
                      title="Toggle 'required' for every field on the canvas"
                      onKeyDown={(e) => (e.key === " " || e.key === "Enter") ? toggleRequiredForAll() : null}
                    >
                      <ListChecks size={13} className="fb-toggle-chip-icon" />
                      <span className="fb-toggle-chip-label">Required for All</span>
                      <div className={`fe-switch ${allRequired ? "fe-switch-on" : ""}`}>
                        <div className="fe-switch-thumb" />
                      </div>
                    </div>

                    <div
                      className={`fb-toggle-chip ${allReceiptEnabled ? "fb-toggle-chip-active" : ""} ${!fields.length ? "fb-toggle-chip-disabled" : ""}`}
                      onClick={toggleReceiptForAll}
                      role="switch"
                      aria-checked={allReceiptEnabled}
                      tabIndex={fields.length ? 0 : -1}
                      title={`Toggle receipt for every field (max ${RECEIPT_FIELD_LIMIT} fields). Amount is always included.`}
                      onKeyDown={(e) => (e.key === " " || e.key === "Enter") ? toggleReceiptForAll() : null}
                    >
                      <Receipt size={13} className="fb-toggle-chip-icon" />
                      <span className="fb-toggle-chip-label">Enable receipt for all fields</span>
                      {receiptCount > 0 && (
                        <span className="fb-toggle-chip-count">{receiptCount}/{RECEIPT_FIELD_LIMIT}</span>
                      )}
                      <div className={`fe-switch ${allReceiptEnabled ? "fe-switch-on" : ""}`}>
                        <div className="fe-switch-thumb" />
                      </div>
                    </div>
                
                  </div>
                </div>

                <button
                  type="button"
                  className={`fb-preview-toggle ${showLivePreview ? "fb-preview-toggle-active" : ""}`}
                  onClick={() => setShowLivePreview((v) => !v)}
                  title={showLivePreview ? "Back to field builder" : "Show live preview"}
                >
                  {showLivePreview ? (
                    <><EyeOff size={14} className="me-1" />Back to Fields</>
                  ) : (
                    <><Eye size={14} className="me-1" />Show Preview</>
                  )}
                </button>
              </CardBody>
            </Card>

            {showLivePreview ? (
              <Card>
                <CardBody>
                  <FormPreview fields={fields} />
                </CardBody>
              </Card>
            ) : (
              <Row className="g-3">
                {/* ── Canvas ─────────────────────────────────────────────── */}
                <Col md={selectedField ? "6" : "12"}>
                  <Card className="h-100">
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">
                          Canvas{" "}
                          <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>
                            ({fields.length}/{MAX_FIELDS})
                          </span>
                        </h6>
                      </div>
                      <Droppable droppableId="canvas">
                        {(provided) => (
                          <div ref={provided.innerRef} {...provided.droppableProps} className="min-vh-25">
                            {fields.map((field, index) => {
                              const nf = {
                                ...field,
                                mandatory: field.isSearchKey ? true : field.mandatory,
                                required: field.isSearchKey ? true : field.required,
                                readOnly: field.readOnly ?? false,
                              };
                              const isSelected = selectedFieldId === nf.id;

                              return (
                                <Draggable
                                  key={nf.id}
                                  draggableId={nf.id}
                                  index={index}
                                  isDragDisabled={!!nf.isSearchKey}
                                >
                                  {(prov, snapshot) => (
                                    <div
                                      ref={prov.innerRef}
                                      {...prov.draggableProps}
                                      {...prov.dragHandleProps}
                                      onClick={() => setSelectedFieldId(isSelected ? null : nf.id)}
                                      className={[
                                        "fb-canvas-field",
                                        isSelected ? "fb-canvas-field-selected" : "",
                                        snapshot.isDragging ? "fb-canvas-field-dragging" : "",
                                        nf.isSearchKey ? "fb-canvas-field-searchkey" : nf.mandatory ? "fb-canvas-field-mandatory" : "",
                                      ].filter(Boolean).join(" ")}
                                      tabIndex={0}
                                    >
                                      <div className="fb-canvas-field-inner">
                                        <div className="fb-canvas-field-drag">
                                          <GripVertical size={14} className="text-muted" />
                                        </div>
                                        <div className="fb-canvas-field-info">
                                          <span className="fb-canvas-field-label">{nf.label || <em className="text-muted">Untitled</em>}</span>
                                          <span className="fb-canvas-field-type">{nf.type}</span>
                                        </div>
                                        <div className="fb-canvas-field-badges">
                                          {field.cascadeEnabled && (
  <span
    className="badge bg-primary"
  >
    Cascade L{field.cascadeLevel}
  </span>
)}
                                          {nf.receiptEnabled &&
                                            nf.receiptOrder != null &&
                                            nf.receiptOrder !== "" && (
                                              <span
                                                className="badge"
                                                style={{
                                                  fontSize: "0.65rem",
                                                  backgroundColor: "#4f46e5",
                                                  color: "#fff",
                                                }}
                                              >
                                                Order: {nf.receiptOrder}
                                              </span>
                                            )}
                                          {nf.isSearchKey && <span className="badge bg-success" style={{ fontSize: "0.65rem" }}>Search Key</span>}
                                          {nf.mandatory && !nf.isSearchKey && <span className="badge bg-danger" style={{ fontSize: "0.65rem" }}>Mandatory</span>}
                                          {nf.required && !nf.mandatory && !nf.isSearchKey && (
                                            <span className="badge bg-primary" style={{ fontSize: "0.65rem" }}>Required</span>
                                          )}
                                          {nf.fromExcel && <span className="badge bg-success" style={{ fontSize: "0.65rem" }}>Excel</span>}
                                          {nf.receiptEnabled && (
                                            <span className="badge" style={{ fontSize: "0.65rem", backgroundColor: "#8b5cf6", color: "#fff" }}>
                                              Receipt
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                            {fields.length === 0 && (
                              <div className="fb-canvas-empty">
                                <p className="text-muted small mb-0">Drag fields here from the palette</p>
                              </div>
                            )}
                          </div>
                        )}
                      </Droppable>
                    </CardBody>
                  </Card>
                </Col>

                {/* ── Properties Panel ───────────────────────────────────── */}
                {selectedField && (
                  <Col md="6">
                    <Card className="h-100 fb-props-card">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h6 className="mb-0">Properties</h6>
                            <small className="text-muted">{selectedField.label || "Untitled"} · {selectedField.type}</small>
                          </div>
                          <button type="button" className="fe-close-btn" onClick={() => setSelectedFieldId(null)} title="Close properties" aria-label="Close properties panel">
                            <X size={14} />
                          </button>
                        </div>
                        <FieldEditor
                          field={selectedField}
                          fields={fields}
                          onUpdate={updateField}
                          onRemove={removeField}
                          hideMandatoryDelete={selectedField.mandatory}
                          mode={mode}
                          onValidationChange={handleFieldValidationChange}
                          searchKey={excelSearchKey}
                          receiptFieldLimit={RECEIPT_FIELD_LIMIT}
                          receiptEnabledCount={receiptCount}
                          // FieldEditor should render the receipt toggle as
                          // permanently ON/disabled when this is true, since
                          // Amount is always included in the receipt.
                          lockReceiptOn={selectedField.type === "amount" && selectedField.mandatory}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            )}

            {/* ── Action row ─────────────────────────────────────────────── */}
            <Card className="mt-3">
              <CardBody>
                <div className="fs-action-row d-flex flex-wrap gap-2">
                  {!saving && !formSaved && (
                    <Button
                      type="button"
                      color="primary"
                      onClick={handleSaveClick}
                      disabled={!canSave}
                    >
                      {id ? "Update" : "Save & Submit"}
                    </Button>
                  )}
                  <Button type="button" color="secondary" onClick={handlePreview} disabled={!previewStatus}>
                    Preview Public URL
                  </Button>
                  {formSaved && (
                    <Button type="button" color="outline-primary" className="fs-view-progress-btn" onClick={() => setShowSaveProgressModal(true)}>
                      <FileSpreadsheet size={14} className="me-1" /> View Progress
                    </Button>
                  )}
                </div>

                {excelDone && (
                  <div className="fs-success-strip mt-2">
                    <CheckCircle2 size={16} color="#16a34a" />
                    <span className="fs-success-strip-text">
                      {hasExcel ? "Form saved and data processed successfully!" : id ? "Form updated successfully!" : "Form created successfully!"}
                    </span>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </DragDropContext>

      {/* ── Confirm modal ─────────────────────────────────────────────────── */}
      <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)} centered>
        <ModalHeader toggle={() => setShowConfirmModal(false)} className="fs-confirm-header">
          {id ? "Confirm Form Update" : "Confirm Form Submission"}
        </ModalHeader>
        <ModalBody className="fs-confirm-body">
          <div className={`fs-env-badge ${environment === "P" ? "fs-env-badge-prod" : "fs-env-badge-uat"}`}>
            <span className={`fs-env-dot ${environment === "P" ? "fs-env-dot-prod" : "fs-env-dot-uat"}`} />
            <span className={`fs-env-label ${environment === "P" ? "fs-env-label-prod" : "fs-env-label-uat"}`}>
              {environment === "P" ? "Production" : "UAT"} Environment
            </span>
          </div>
          <div className="fs-summary-label">Form Summary</div>
          <div className="fs-summary-card">
            <div className="fs-summary-row">
              <div className="fs-summary-row-left">
                <div className="fs-summary-total-badge"><span className="fs-summary-total-num">{fields.length}</span></div>
                <span className="fs-summary-row-title">Total Fields</span>
              </div>
              <span className="fs-summary-row-right">{MAX_FIELDS - fields.length} slots remaining</span>
            </div>
            <div className="fs-summary-row fs-summary-row-border">
              <div className="fs-summary-row-left">
                <CheckCircle size={16} color="#16a34a" />
                <span className="fs-summary-row-title">Mandatory Fields</span>
              </div>
              <span className="fs-summary-pill-green">{mandatoryCount}</span>
            </div>
            <div className="fs-summary-row fs-summary-row-border">
              <div className="fs-summary-row-left">
                <Circle size={16} color="#9ca3af" />
                <span className="fs-summary-row-title">Optional Fields</span>
              </div>
              <span className="fs-summary-pill-grey">{optionalCount}</span>
            </div>
            <div className="fs-summary-row fs-summary-row-border">
              <div className="fs-summary-row-left">
                <Receipt size={16} color="#6366f1" />
                <span className="fs-summary-row-title">Receipt-enabled Fields</span>
              </div>
              <span className="fs-summary-pill-grey">{receiptCount}/{RECEIPT_FIELD_LIMIT}</span>
            </div>
          </div>
          {hasExcel && (
            <div className="fs-excel-note">
              <FileSpreadsheet size={15} color="#16a34a" style={{ flexShrink: 0 }} />
              <span className="fs-excel-note-text">Excel master data will be processed after the form is created.</span>
            </div>
          )}
        </ModalBody>
        <ModalFooter className="fs-confirm-footer">
          <Button type="button" color="secondary" outline onClick={() => setShowConfirmModal(false)}>Cancel</Button>
          <Button type="button" color="primary" onClick={onConfirmSave} className="fw-semibold">
            {id ? "Yes, Update" : "Yes, Publish Form"}
          </Button>
        </ModalFooter>
      </Modal>

      <SaveProgressModal
        isOpen={showSaveProgressModal}
        runKey={saveRunKey}
        slug={slug}
        doCreateForm={doCreateForm}
        hasExcel={hasExcel}
        onClose={() => { setShowSaveProgressModal(false); setSaving(false); }}
        onFullSuccess={() => {
          setExcelDone(true);
          setSaving(false);
          ["excel_rows", "excel_file_meta", "excel_fields"].forEach((k) => localStorage.removeItem(k));
        }}
      />
    </div>
  );
}
